/* eslint-disable no-console */
import { useCallback, useState } from 'react';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { useRouter } from '@cp/shared/router';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { useClientProfile } from 'src/entities/client';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { logError, resetLoading, toError } from '../lib/result';

const namespace = 'ConnectCatalogItem';

export type ConnectCatalogItemFeature = (catalogItemId: number) => AsyncResult<void, Error>;

export const useConnectCatalogItem = (): [ConnectCatalogItemFeature, boolean] => {
  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();
  const resolver = useClientWebResolver();
  const clientProfile = useClientProfile();
  const [queryCatalogItem] = coreCatalogItemApi.endpoints.getById.useLazyQuery();
  const [connectWithAuthor] = coreCatalogItemApi.endpoints.connectWithAuthor.useMutation();

  const connectFeature = useCallback<ConnectCatalogItemFeature>(
    async (catalogItemId) => {
      console.debug(`[${namespace}]: Starting the proccess with ${catalogItemId}...`);
      setIsLoading(true);

      const connectionAllowed = await Result.fromAsync(() => queryCatalogItem({ id: catalogItemId }).unwrap(), toError).then((result) =>
        result
          .map((catalogItem) => !!clientProfile?.type && clientTypeToSuitableCatalogItemType(clientProfile.type) === catalogItem.type)
          .tapError(resetLoading(setIsLoading))
          .tapError(logError(namespace)),
      );

      if (!connectionAllowed.unwrapOrElse(resetLoading(setIsLoading))) {
        return Result.Err(new Error('Invalid type of catalog item or client profile'));
      }

      const connect = await Result.fromAsync(() => connectWithAuthor({ id: catalogItemId }).unwrap(), toError);
      return connect
        .tapError(logError(namespace))
        .tapError(resetLoading(setIsLoading))
        .tap(resetLoading(setIsLoading))
        .tap(() => console.log(`[${namespace}]: The process was completed successfully!`))
        .mapAsync(({ dialogId }) => router.navigate(resolver.resolve({ page: ClientWebPage.ProfileMessages, queryParams: { dialogId } })));
    },
    [queryCatalogItem, clientProfile?.type, connectWithAuthor, router, resolver],
  );

  return [connectFeature, isLoading];
};
